import { useState, useCallback, useRef, useEffect } from 'react';
import { useMapStore } from '../../lib/map/store';
import { useLockManager } from '../../lib/map/lockManager';
import { standardizeLocation, areLocationsNearlyIdentical } from '../../lib/map/locationUtils';

interface LocationOption {
  name: string;
  address: string;
}

// NearbyLocations component that uses Zustand store
export const NearbyLocations: React.FC = () => {
  const { 
    alternateAddresses, 
    searchAddress,
    updatePosition, 
    setLocationName, 
    setAddress, 
    map
  } = useMapStore();
  const [expanded, setExpanded] = useState(true);
  
  // Add a state to track whether location selection is in progress
  const [isSelecting, setIsSelecting] = useState(false);
  
  // Access the removeIdleListener and addIdleListener functions
  const mapRef = useRef<google.maps.Map | null>(null);
  const idleListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  
  // 记录上次更新的时间戳，防止频繁展开收起
  const lastUpdateRef = useRef<number>(0);
  
  // Auto-expand when nearby places are available
  useEffect(() => {
    // 检查是否有有效的位置选项
    const validLocations = alternateAddresses.filter(
      loc => loc && loc.name && loc.address
    );
    
    if (validLocations.length > 0) {
      console.log('[NearbyLocations] Found', validLocations.length, 'valid nearby places');
      
      // 只有当新地点和时间间隔超过1秒时才自动展开
      const now = Date.now();
      if (now - lastUpdateRef.current > 1000) {
        console.log('[NearbyLocations] Auto-expanding panel');
        setExpanded(true);
        lastUpdateRef.current = now;
      }
    } else {
      console.log('[NearbyLocations] No valid nearby places found');
    }
  }, [alternateAddresses]);
  
  // Set mapRef when map changes
  useEffect(() => {
    if (map) {
      mapRef.current = map;
    }
  }, [map]);
  
  // Function to remove idle listener safely
  const removeIdleListener = useCallback(() => {
    if (idleListenerRef.current) {
      google.maps.event.removeListener(idleListenerRef.current);
      idleListenerRef.current = null;
      console.log('Removed idle listener');
    }
  }, []);
  
  // Function to add idle listener with safeguards
  const addIdleListener = useCallback(() => {
    if (!mapRef.current) return;
    
    // Remove existing listener if any
    if (idleListenerRef.current) {
      google.maps.event.removeListener(idleListenerRef.current);
      idleListenerRef.current = null;
    }
    
    // Add fresh listener
    idleListenerRef.current = mapRef.current.addListener('idle', () => {
      // We don't need to implement the full handleMapMoveEnd logic here
      // The MapLocationModal component will handle that
      console.log('Map idle event detected');
    });
    console.log('Added idle listener for map movements');
  }, []);

  // This function directly uses the store to handle location selection
  const handleSelectLocation = useCallback((location: LocationOption) => {
    // Standardize location format
    const standardLocation = {
      position: { lat: 0, lng: 0 }, // Will be populated by search
      address: location.address,
      locationName: location.name
    };
    
    // Update UI state
    setLocationName(standardLocation.locationName);
    setAddress(standardLocation.address);
    
    // Get coordinates for the selected address and update the map position
    searchAddress(standardLocation.address)
      .then(result => {
        console.log('[DEBUG] NearbyLocations - Found position for address:', result.position);
        
        // Check if the new position is nearly identical to current position
        const currentPosition = useMapStore.getState().markerPosition;
        const isNearIdentical = areLocationsNearlyIdentical(result.position, currentPosition);
        
        if (isNearIdentical) {
          console.log('[DEBUG] NearbyLocations - New position nearly identical, skipping map update');
          // Even if position is the same, we still update the address
          // Reset locks and isSelecting state
          useLockManager.getState().unlockMultiple(['address', 'map', 'position'], 'position unchanged');
          useMapStore.setState({ isProgrammaticUpdate: false });
          setIsSelecting(false);
          setExpanded(false);
          return;
        }
        
        // Update position state
        updatePosition(result.position);
        
        // Update map position
        if (map) {
          // Remove idle listener to prevent multiple updates
          removeIdleListener();
          
          // Move the map to the selected position
          map.panTo(result.position);
          
          // Ensure the map center is exactly at the target position
          map.setCenter(result.position);
          
          // Trigger a resize to ensure the map is rendered correctly
          setTimeout(() => {
            if (map) {
              google.maps.event.trigger(map, 'resize');
              
              // Double check the center is correct
              map.setCenter(result.position);
              
              // Re-add idle listener after a delay
              setTimeout(() => {
                // Reset locks before adding the idle listener
                useLockManager.getState().unlockMultiple(['address', 'map', 'position'], 'nearby location selection complete');
                
                // For backwards compatibility
                useMapStore.setState({ isProgrammaticUpdate: false });
                console.log('[DEBUG] NearbyLocations - isProgrammaticUpdate reset to false');
                
                // Add the idle listener after the flag is reset
                addIdleListener();
                
                // Reset the selection state
                setIsSelecting(false);
              }, 1000); // Longer delay to ensure everything has settled
            }
          }, 300); // Delay for resize
        } else {
          // If no map, still reset locks after a delay
          setTimeout(() => {
            useLockManager.getState().unlockMultiple(['address', 'map', 'position'], 'nearby location selection complete (no map)');
            
            // For backwards compatibility
            useMapStore.setState({ isProgrammaticUpdate: false });
            console.log('[DEBUG] NearbyLocations - No map - isProgrammaticUpdate reset to false');
            
            // Reset the selection state
            setIsSelecting(false);
          }, 1000);
        }
      })
      .catch(error => {
        console.error('Error getting coordinates for selected address', error);
        // Ensure locks are reset even in case of error, but with a delay
        setTimeout(() => {
          useLockManager.getState().unlockMultiple(['address', 'map', 'position'], 'nearby location selection error');
          
          // For backwards compatibility
          useMapStore.setState({ isProgrammaticUpdate: false });
          console.log('[DEBUG] NearbyLocations - Error - isProgrammaticUpdate reset to false');
          
          // Reset the selection state
          setIsSelecting(false);
        }, 1000);
      });
    
    // Auto-collapse after selection
    setExpanded(false);
  }, [searchAddress, updatePosition, setLocationName, setAddress, map, removeIdleListener, addIdleListener]);

  // Debounced function for location selection
  const handleLocationClick = useCallback((location: LocationOption) => {
    // Prevent multiple rapid clicks
    if (isSelecting) {
      console.log('[DEBUG] NearbyLocations - Ignoring click, selection already in progress');
      return;
    }
    
    // Mark selection as in progress
    setIsSelecting(true);
    
    // Use the lock manager for consistent locking
    const lockManager = useLockManager.getState();
    lockManager.lockMultiple(['address', 'map', 'position'], 'nearby location selection');
    
    // For backwards compatibility
    useMapStore.setState({ isProgrammaticUpdate: true });
    console.log('[DEBUG] NearbyLocations - isProgrammaticUpdate set to true');
    
    // Use setTimeout to add a slight delay before handling the selection
    setTimeout(() => {
      handleSelectLocation(location);
    }, 100);
  }, [isSelecting, handleSelectLocation]);

  if (alternateAddresses.length === 0) {
    return null;
  }

  return (
    <div className="mb-4 mx-4 border border-gray-200 rounded-lg overflow-hidden shadow-md">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="flex w-full justify-between items-center py-3 px-4 bg-gradient-to-r from-yellow-50 to-yellow-100 hover:from-yellow-100 hover:to-yellow-200 text-sm font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-400"
        aria-expanded={expanded}
        aria-controls="nearby-locations-list"
      >
        <div className="flex items-center">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            className="mr-2 text-yellow-600"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          <span className="font-semibold">Nearby locations <span className="inline-block bg-yellow-300 text-yellow-800 text-xs font-bold px-2 py-0.5 rounded-full ml-1">{alternateAddresses.length}</span></span>
        </div>
        <div className="flex items-center">
          <span className="text-xs font-medium text-yellow-700 mr-2">
            {expanded ? 'Hide options' : 'View options'}
          </span>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            fill="currentColor" 
            viewBox="0 0 16 16"
            className={`transition-transform duration-200 text-yellow-600 ${expanded ? 'rotate-180' : ''}`}
          >
            <path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
          </svg>
        </div>
      </button>
      
      {expanded && (
        <div id="nearby-locations-list" className="p-3 bg-white border-t border-gray-100">
          <div className="space-y-2">
            {alternateAddresses.map((location, index) => (
              <div 
                key={index} 
                className={`p-3 border border-gray-200 rounded-lg ${isSelecting ? 'opacity-70 cursor-wait bg-gray-50' : 'cursor-pointer bg-white hover:bg-yellow-50 hover:border-yellow-300 hover:shadow-sm'} transition duration-150 ease-in-out`}
                onClick={() => !isSelecting && handleLocationClick(location)}
              >
                <div className="flex items-start">
                  <div className="mr-3 mt-0.5 text-yellow-500">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-800">{location.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{location.address}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}; 